import { useEffect, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutData } from '@/mocks/about';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: aboutData.brandName,
    url: 'https://www.wukongyingshi.com',
    description: '悟空影视免费观看平台，提供高清电影、电视剧、动漫、综艺在线观看服务，无需注册即点即播。',
    foundingDate: aboutData.foundedYear,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: aboutData.contactInfo.phone,
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
      email: aboutData.contactInfo.email,
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: aboutData.contactInfo.address,
      addressLocality: '北京',
      addressRegion: '北京',
      addressCountry: 'CN',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutData.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>
          </div>

          {/* Hero Banner */}
          <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Cinematic%20movie%20production%20set%20with%20warm%20golden%20lighting%2C%20film%20reels%20and%20vintage%20projector%2C%20creative%20workspace%20atmosphere%2C%20art%20deco%20elements%2C%20professional%20cinema%20equipment%2C%20elegant%20and%20warm%20tones&width=1800&height=800&seq=about-hero-wukong&orientation=landscape"
                alt="悟空影视品牌介绍"
                title="悟空影视 - 关于我们"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">
                关于悟空影视
              </h1>
              <p className="text-base md:text-lg text-white/80 font-light max-w-xl">
                {aboutData.brandSlogan}
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full px-4 md:px-6 py-8 md:py-10">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {aboutData.stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="text-center p-5 rounded-xl bg-background-100 border border-background-200/70"
                  >
                    <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500">
                      {stat.value}
                    </p>
                    <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Brand Intro */}
          <section className="w-full px-4 md:px-6 py-8 md:py-10">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                品牌故事
              </h2>
              <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed">
                {aboutData.introduction.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="mb-4">{paragraph}</p>
                ))}
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2 text-center">
                我们的优势
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                为什么选择悟空影视
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {aboutData.features.map((feature) => (
                  <div
                    key={feature.title}
                    className="p-5 rounded-xl bg-background-50 border border-background-200/70"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 mb-4">
                      <i className={`${feature.icon} text-lg text-primary-600`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="w-full px-4 md:px-6 py-8 md:py-10">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2 text-center">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                来自全国各地的真实用户评价
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {aboutData.testimonials.map((t, idx) => (
                  <div
                    key={idx}
                    className="p-5 rounded-xl bg-background-100 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className={`text-xs ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                      &ldquo;{t.content}&rdquo;
                    </p>
                    <p className="text-xs text-foreground-500">
                      {t.name} · {t.location}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2 text-center">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                关于悟空影视的常见疑问解答
              </p>
              <div className="space-y-3">
                {aboutData.faqs.map((faq, idx) => (
                  <details
                    key={idx}
                    className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <h3 className="text-sm font-medium text-foreground-800 pr-4">
                        {faq.question}
                      </h3>
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="w-full px-4 md:px-6 py-8 md:py-10">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2 text-center">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                有任何问题或建议，欢迎随时联系我们
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                      <p className="text-sm text-foreground-500">{aboutData.contactInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-phone-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">客服电话</p>
                      <p className="text-sm text-foreground-500">{aboutData.contactInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-wechat-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">微信公众号</p>
                      <p className="text-sm text-foreground-500">{aboutData.contactInfo.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">公司地址</p>
                      <p className="text-sm text-foreground-500">{aboutData.contactInfo.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-time-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">工作时间</p>
                      <p className="text-sm text-foreground-500">{aboutData.contactInfo.workHours}</p>
                    </div>
                  </div>
                </div>
                <div className="rounded-xl bg-background-100 border border-background-200/70 p-6">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                    发送消息
                  </h3>
                  <ContactForm />
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}

function ContactForm() {
  const formUrl = 'https://readdy.ai/api/form/d9nfhmgbdkpk99j7gir0';

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      const statusEl = document.getElementById('contact-form-status');
      if (statusEl) statusEl.textContent = '消息已发送，我们会尽快回复您！';
      return;
    }
    const formData = new FormData(form);
    fetch(formUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
    })
      .then((res) => res.text())
      .then(() => {
        const statusEl = document.getElementById('contact-form-status');
        if (statusEl) {
          statusEl.textContent = '消息已发送，我们会尽快回复您！';
          statusEl.className = 'text-sm text-green-600 mt-3';
        }
        form.reset();
      })
      .catch(() => {
        const statusEl = document.getElementById('contact-form-status');
        if (statusEl) {
          statusEl.textContent = '发送失败，请稍后重试或直接发送邮件联系我们。';
          statusEl.className = 'text-sm text-red-500 mt-3';
        }
      });
  };

  return (
    <form onSubmit={handleSubmit} id="contact-form" data-readdy-form>
      <div className="space-y-4">
        <div>
          <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1">姓名</label>
          <input
            type="text"
            id="contact-name"
            name="name"
            required
            className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 outline-none focus:border-primary-300 transition-colors"
            placeholder="请输入您的姓名"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1">邮箱</label>
          <input
            type="email"
            id="contact-email"
            name="email"
            required
            className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 outline-none focus:border-primary-300 transition-colors"
            placeholder="请输入您的邮箱"
          />
        </div>
        <div>
          <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1">留言内容</label>
          <textarea
            id="contact-message"
            name="message"
            required
            maxLength={500}
            rows={4}
            className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 outline-none focus:border-primary-300 transition-colors resize-none"
            placeholder="请输入您的留言（最多500字）"
          ></textarea>
        </div>
        <div className="honeypot-field">
          <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
        </div>
        <button
          type="submit"
          className="w-full px-5 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          发送消息
        </button>
        <p id="contact-form-status" className="text-sm mt-3"></p>
      </div>
    </form>
  );
}